// synthetic-arabic: reviewed-no-scripture — the Arabic here is ordinary product interface wording invented for this application; it contains no Qur'an, hadith, translation or religious quotation.
import { LANGUAGES, messages, useMessages, type Language } from '../i18n.js';

const T = messages({
  ar: { group: 'اللغة' },
  en: { group: 'Language' },
});

/** Each language named in itself: the label has to be readable by the person it is for. */
const LANGUAGE_NAMES: Readonly<Record<Language, string>> = Object.freeze({
  ar: 'العربية',
  en: 'English',
});

/**
 * The two-way language choice, wherever a person can be before the settings are reachable: the
 * introduction (founder, 2026-09-20) and every card of the door (founder, 2026-09-21).
 *
 * One component, because it is one decision. It is the settings' choice in a smaller coat — the
 * caller hands it the same `chooseLanguage` — so what is pressed here is what the settings show
 * afterwards. There is no "automatic" here on purpose: a person pressing this wants a language,
 * and the three-way choice lives in the settings for whoever wants the browser to decide.
 *
 * Each button carries its own `lang`, so a screen reader pronounces the name in the language it
 * is written in whichever language the card around it speaks.
 */
export function LanguageSwitch({
  language,
  onLanguage,
}: {
  readonly language: Language;
  readonly onLanguage: (language: Language) => void;
}): React.JSX.Element {
  const t = useMessages(T);
  return (
    <div className="segmented language-switch" role="group" aria-label={t.group}>
      {LANGUAGES.map((choice) => (
        <button
          key={choice}
          type="button"
          className="language-choice"
          lang={choice}
          aria-pressed={language === choice}
          onClick={() => {
            onLanguage(choice);
          }}
        >
          {LANGUAGE_NAMES[choice]}
        </button>
      ))}
    </div>
  );
}
